import { clone, isEqual } from 'lodash';
import { Subject } from 'rxjs';

import LocalData from '@issue-detail/components/IssueDialog/localData';
import { isEmpty } from '@issue-detail/utils';
import { fromLocalCacheOrRemote } from '@issue-detail/utils/useIndexedDb';

import {
  getIssueAssembleInfo,
  getIssueLayout,
  getIssueTypes,
  getSprints
} from '../Api/issue';
import {
  IssueArea,
  IssueAssembleResponse,
  IssueRelationship,
  IssueSubTask,
  IssueType,
  Sprint
} from '../Model/Issue';

export const getIssueTypesWarper = async (
  db: LocalData | undefined,
  projectId: number
) =>
  fromLocalCacheOrRemote<IssueType[]>(
    db,
    db => db.getLocalIssueTypes(projectId),
    () => getIssueTypes(projectId)
  );

export const getSprintsWarper = async (
  db: LocalData | undefined,
  projectId: number
) =>
  fromLocalCacheOrRemote<Sprint[]>(
    db,
    async db =>
      ((await db.getLocalSprints(projectId)) as Sprint[]).filter(
        s => !s.closed
      ),
    () => getSprints(projectId)
  );

export const getFieldLayoutWarper = async (
  db: LocalData | undefined,
  issueTypeId: number,
  projectId: number
) =>
  fromLocalCacheOrRemote<IssueArea[]>(
    db,
    async db => (await db.getLocalLayout(issueTypeId)) as IssueArea[],
    () => getIssueLayout(projectId, issueTypeId)
  );

export const getIssueDetailObservable$ = (
  db: LocalData | undefined,
  issueId: number,
  force = false
) => {
  const subject = new Subject<IssueAssembleResponse>();
  getIssueDetailWarper(db, issueId, subject, force);
  return subject.asObservable();
};

export const getIssueDetailWarper = async (
  db: LocalData | undefined,
  issueId: number,
  subject: Subject<IssueAssembleResponse>,
  force = false
) => {
  const result: IssueAssembleResponse = {} as IssueAssembleResponse;
  if (db && !force) {
    const issueValues = (await db.getLocalIssue(issueId)) as Record<
      string,
      any
    >;
    if (issueValues?.id) {
      result.issueVO = {
        id: issueValues.id,
        createTime: issueValues.createTime,
        data: issueValues,
        projectId: issueValues.projectId,
        updateTime: issueValues.updateTime
      };

      await Promise.all([
        db
          .getLocalIssueTypes(result.issueVO.projectId)
          .then((issueTypes?: IssueType[]) => {
            result!.issueTypeVOList = issueTypes;
          })
          .catch(),
        db
          .getLocalSprints(result.issueVO.projectId)
          .then((sprints?: Sprint[]) => {
            result!.sprintVOList = sprints;
          })
          .catch(),
        db
          .getLocalLayout(result.issueVO.data.issueTypeId)
          .then((res?: { issueAreaVOList: IssueArea[],updateTime:number }) => {
            if (res) {
              result!.issueAreaVOList = res.issueAreaVOList;
              result!.cachedLayoutLastModifiedTime = res.updateTime;
            }
          })
          .catch(),
        // db
        //   .getLocalRelation(issueId)
        //   .then((relations?: IssueRelationship[]) => {
        //     result!.issueRelationshipVOList = relations;
        //   })
        //   .catch(),
        db
          .getLocalSubtasks(issueId)
          .then((subtasks?: Record<string, any>) => {
            if (subtasks) {
              result!.issueSubTaskVOList = subtasks.map((task: any) => ({
                ...task,
                statusId: task.statusVO.id,
                status: task.statusVO
              })) as IssueSubTask[];
            }
          })
          .catch()
      ]).catch();
    }
  }

  if (Object.keys(result).length) {
    subject.next(clone(result));
  }

  try {
    const res = await getIssueAssembleInfo(
      {
        issueId,
        issueTypeId: result.issueVO?.data?.issueTypeId,
        projectId: result.issueVO?.data?.projectId
      },
      {
        loadIssueData: !result?.issueVO,
        loadIssueRelationship: !result?.issueRelationshipVOList,
        loadIssueSubTask: !result?.issueSubTaskVOList,
        loadIssueType: !result?.issueTypeVOList?.length,
        loadLayoutData: !result?.issueAreaVOList?.length,
        loadSprint: !result?.sprintVOList?.length
      },
      result.issueVO?.updateTime,
      result?.cachedLayoutLastModifiedTime
    );
    if (res.deleteIssueExtendVO) {
      subject.error(res);
      return;
    }
    Object.keys(res).forEach(key => {
      const k = key as keyof IssueAssembleResponse;
      if (typeof result[k] === 'undefined' || !isEmpty(res[k])) {
        result[k] = res[k] as any;
      }
    });
    subject.next(clone(result));
    subject.complete();
  } catch (error) {
    subject.error(error);
  }
};

export const getCreateIssueInfoWarper$ = (
  db: LocalData | undefined,
  projectId: number,
  issueTypeId?: number,
  issueTypes?: IssueType[],
  force = false
) => {
  const subject = new Subject<IssueAssembleResponse>();
  getCreateIssueInfoWarper(
    db,
    projectId,
    issueTypeId,
    subject,
    issueTypes,
    force
  );
  return subject.asObservable();
};

export const getCreateIssueInfoWarper = async (
  db: LocalData | undefined,
  projectId: number,
  issueTypeId: number | undefined,
  subject: Subject<IssueAssembleResponse>,
  issueTypes: IssueType[] | undefined,
  force = false
) => {
  const result: IssueAssembleResponse = {} as IssueAssembleResponse;
  result.issueTypeVOList = issueTypes;
  if (db && !force) {
    await Promise.all([
      // db
      //   .getLocalIssueTypes(projectId)
      //   .then((issueTypes?: IssueType[]) => {
      //     result!.issueTypeVOList = issueTypes;
      //   })
      //   .catch(),
      db
        .getLocalSprints(projectId)
        .then((sprints?: Sprint[]) => {
          result!.sprintVOList = sprints;
        })
        .catch(),
      issueTypeId &&
        db
          .getLocalLayout(issueTypeId)
          .then((res?: { issueAreaVOList: IssueArea[] }) => {
            if (res) {
              result!.issueAreaVOList = res.issueAreaVOList;
            }
          })
          .catch()
    ]).catch();
  }

  if (Object.keys(result).length) {
    subject.next(clone(result));
  }

  const res = await getIssueAssembleInfo(
    { projectId, issueTypeId },
    {
      loadIssueType: !result?.issueTypeVOList?.length,
      loadLayoutData: !result?.issueAreaVOList?.length,
      loadSprint: !result?.sprintVOList?.length
    }
  );
  Object.keys(res).forEach(key => {
    const k = key as keyof IssueAssembleResponse;
    if (typeof result[k] === 'undefined' || !isEmpty(res[k])) {
      result[k] = res[k] as any;
    }
  });
  subject.next(clone(result));
  subject.complete();
};
